import React, { forwardRef, ChangeEvent } from 'react';
import { useLang } from '../LangContext';

type FormField = 'customerName' | 'customerEmail' | 'customerDescription';

interface FieldErrors {
  customerName?: string;
  customerEmail?: string;
  general?: string;
}

interface Props {
  customerName: string;
  customerEmail: string;
  customerDescription: string;
  onChange: (field: FormField, value: string) => void;
  onSubmit: () => void;
  error: string;
  fieldErrors: FieldErrors;
  isSubmitting: boolean;
}

const CustomerForm = forwardRef<HTMLDivElement, Props>(function CustomerForm(
  {
    customerName,
    customerEmail,
    customerDescription,
    onChange,
    onSubmit,
    error,
    fieldErrors,
    isSubmitting,
  },
  ref,
) {
  const { t } = useLang();

  return (
    <div ref={ref} className="booking-form">
      <h4>{t.bookYourAppointment}</h4>

      {error && (
        <div id="form-general-error" className="error-message" role="alert">
          <strong>{t.errorLabel}</strong> {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="customerName">{t.yourName}</label>
        <input
          id="customerName"
          type="text"
          value={customerName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('customerName', e.target.value)}
          placeholder={t.enterYourName}
          className={fieldErrors.customerName ? 'error' : ''}
          disabled={isSubmitting}
          aria-required="true"
          aria-invalid={!!fieldErrors.customerName}
          aria-describedby={fieldErrors.customerName ? 'error-customerName' : undefined}
        />
        {fieldErrors.customerName && (
          <span id="error-customerName" className="field-error" role="alert">{fieldErrors.customerName}</span>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="customerEmail">{t.yourEmail}</label>
        <input
          id="customerEmail"
          type="email"
          value={customerEmail}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange('customerEmail', e.target.value)}
          placeholder={t.enterYourEmail}
          className={fieldErrors.customerEmail ? 'error' : ''}
          disabled={isSubmitting}
          aria-required="true"
          aria-invalid={!!fieldErrors.customerEmail}
          aria-describedby={fieldErrors.customerEmail ? 'error-customerEmail' : undefined}
        />
        {fieldErrors.customerEmail && (
          <span id="error-customerEmail" className="field-error" role="alert">{fieldErrors.customerEmail}</span>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="customerDescription">{t.descriptionOptional}</label>
        <textarea
          id="customerDescription"
          value={customerDescription}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange('customerDescription', e.target.value)}
          placeholder={t.descriptionPlaceholder}
          rows={3}
          maxLength={300}
          disabled={isSubmitting}
        />
        <small className="character-count">{customerDescription.length}/300 {t.characters}</small>
      </div>
      <button className="submit-button" onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? t.bookingInProgress : t.bookAppointment}
      </button>
    </div>
  );
});

export default CustomerForm;
